/**
 * i18next bootstrap (TMMRSL-0005). Catalogs are bundled at build time — CSP-clean,
 * no CDN, no runtime fetch. One namespace (`translation`). Initial language: the
 * stored preference, else the browser language, else `en`. Every language change
 * persists the preference and sets `<html lang>`. Ported from tmm-app TMMAPP-0001.
 */
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LOCALE, intlLocale, normalizeLocale, SUPPORTED_LOCALES } from './constants';
import { readStoredLocale, writeStoredLocale } from './localePreference';
import en from '@/locales/en.json';
import es from '@/locales/es.json';
import fr from '@/locales/fr.json';
import pt from '@/locales/pt.json';

const RESOURCES = {
  en: { translation: en },
  es: { translation: es },
  fr: { translation: fr },
  pt: { translation: pt },
} as const;

export function resolveInitialLocale(): string {
  return (
    readStoredLocale() ??
    normalizeLocale(typeof navigator !== 'undefined' ? navigator.language : null) ??
    DEFAULT_LOCALE
  );
}

function applyLanguage(lng: string) {
  const normalized = normalizeLocale(lng) ?? DEFAULT_LOCALE;
  writeStoredLocale(normalized);
  document.documentElement.lang = intlLocale(normalized);
}

if (!i18next.isInitialized) {
  const initial = resolveInitialLocale();
  document.documentElement.lang = intlLocale(initial);
  i18next.on('languageChanged', applyLanguage);
  void i18next.use(initReactI18next).init(
    {
      resources: RESOURCES,
      lng: initial,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      nonExplicitSupportedLngs: true,
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
    },
    () => {
      // Catalog selection stays `pt`; number interpolation presents explicitly as pt-BR.
      i18next.services.formatter?.addCached('number', (locale, options) => {
        const formatter = new Intl.NumberFormat(intlLocale(locale ?? DEFAULT_LOCALE), options);
        return (value: number) => formatter.format(value);
      });
    },
  );
}

export default i18next;
